/**
 * WorkspaceKey — the workspace's data key at
 * `workspaces/{orgId}/settings/workspaceKey`, stored only in wrapped form:
 * 32 random bytes, encrypted by the Cloud KMS wrapping key that never
 * leaves KMS. The functions workspace's kms.api.ts owns the
 * wrap/unwrap seam and mints this doc on a workspace's first encrypt; the
 * ciphertext on every `workspaces/{orgId}/integrations/{id}` doc
 * (integration.entity.ts) is sealed under this key. Declares zero actions
 * — no `read`, no `write` of any kind — so the emitted rules carry no
 * `match` block for this path at all and the default-deny tail
 * (`match /{document=**} { allow read, write: if false; }`) covers it: no
 * client can ever read or write this doc; only the Admin SDK touches it.
 *
 * Fields justified:
 *   wrapped — the workspace data key, base64, encrypted by the KMS
 *             wrapping key (or carrying the emulator mark locally); the
 *             only form the key ever takes at rest.
 */
import { entity } from "@mhmo91/ican";
import { firestore } from "@mhmo91/ican/firestore/binding";
import { z } from "zod";
import type { Actor } from "./workspace.entity.ts";

export const WorkspaceKeySchema = z.object({ wrapped: z.string().min(1) });
export type WorkspaceKey = z.infer<typeof WorkspaceKeySchema>;

export const WorkspaceKey = entity<Actor>()({
	schema: WorkspaceKeySchema,
	actions: [] as const,
})
	.pipe(() => [])
	.adaptor(
		firestore({
			hierarchicalGrants: true,
			path: "workspaces/{orgId}/settings/workspaceKey",
		}),
	);
